import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Building, Shield, Users, Briefcase, LogIn } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

const LoginPage: React.FC = () => {
  const { login, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <LoadingSpinner message="Inicializando autenticación..." />
      </div>
    );
  }

  const features = [
    {
      icon: Building,
      title: 'Gestión por Áreas',
      description: 'Organiza servicios y equipos por unidades de negocio'
    },
    {
      icon: Users,
      title: 'Organigramas Interactivos',
      description: 'Visualiza la estructura jerárquica de cada área'
    },
    {
      icon: Briefcase,
      title: 'Catálogo de Servicios',
      description: 'Explora tecnologías y capacidades disponibles'
    },
    {
      icon: Shield,
      title: 'Acceso Seguro',
      description: 'Autenticación integrada con Office 365'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Panel - Branding */}
        <div className="lg:w-1/2 flex items-center justify-center p-8">
          <div className="max-w-md">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="text-white" size={40} />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Readyhub
              </h1>
              <p className="text-xl text-gray-600">
                Dashboard Profesional de Servicios
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-blue-600" size={16} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Panel - Login */}
        <div className="lg:w-1/2 flex items-center justify-center p-8 bg-white lg:shadow-2xl">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Bienvenido
              </h2>
              <p className="text-gray-600">
                Ingresa con tu cuenta corporativa de Office 365
              </p>
            </div>

            {/* Login Card */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Acceso Seguro
                </h3>
                <p className="text-sm text-gray-600">
                  Utiliza tu cuenta de Microsoft para acceder al dashboard
                </p>
              </div>

              <button
                onClick={login}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                    Conectando...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-3" size={20} />
                    Iniciar Sesión con Office 365
                  </>
                )}
              </button>
            </div>

            {/* Security Notice */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Tu información se mantiene segura y privada. <br />
                Solo accedemos a datos básicos de tu perfil corporativo.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;